/**
 * ReadOnlyGuard — wraps action elements, disabling them when read-only
 *
 * Usage:
 *   <ReadOnlyGuard moduleId="insurance" tabId="contracts">
 *     <button onClick={handleDelete}>Supprimer</button>
 *   </ReadOnlyGuard>
 *
 * If the profile is read-only or canWrite is false, the children are rendered
 * with opacity-50 and pointer-events-none.
 */

import React from 'react';
import { useProfile } from '../../context/ProfileContext.jsx';

export function ReadOnlyGuard({ children, moduleId, tabId, className = '' }) {
  const { canWrite, isReadOnly } = useProfile();

  const isDisabled = isReadOnly || !canWrite(moduleId, tabId);

  if (!isDisabled) return children;

  return (
    <div
      className={`opacity-50 pointer-events-none select-none ${className}`}
      title="Lecture seule"
      aria-disabled="true"
    >
      {children}
    </div>
  );
}

/**
 * useReadOnly — hook for complex cases where a wrapper component is not practical
 *
 * Usage:
 *   const { isDisabled, guardProps } = useReadOnly('insurance', 'contracts');
 *   <button {...guardProps} onClick={isDisabled ? undefined : handleClick}>Save</button>
 */
export function useReadOnly(moduleId, tabId) {
  const { canWrite, isReadOnly } = useProfile();

  const isDisabled = isReadOnly || !canWrite(moduleId, tabId);

  const guardProps = isDisabled ? {
    disabled: true,
    className: 'opacity-50 pointer-events-none',
    'aria-disabled': true,
    title: 'Lecture seule',
  } : {};

  return { isDisabled, isReadOnly, guardProps };
}

export default ReadOnlyGuard;
